// QA #7: admin Reports page. Lists every gamic report with admin actions
// (view gamic, restore from hold, manually hide). Backend route
// /gamic/reports already exists, gated by MODERATION_ROLES.
import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '@/services/apiService';

type GamicStatus = 'pending' | 'approved' | 'rejected' | 'hold';

interface ReportRow {
  id: string;
  reported_at: string;
  description: string | null;
  reason_category: string;
  reason_category_id: number;
  reporter_id: string;
  reporter_name: string;
  reporter_email: string;
  reporter_pic: string | null;
  gamic_id: string;
  gamic_title: string;
  gamic_image: string | null;
  gamic_status: GamicStatus;
  gamic_owner_id: string;
  gamic_owner_name: string;
  gamic_owner_email: string;
}

const fetchReports = async (
  page: number,
  search: string,
): Promise<{ rows: ReportRow[]; count: number }> => {
  const api = apiService();
  const res = await api.get('/gamic/reports', {
    params: {
      page,
      limit: 20,
      ...(search ? { search } : {}),
    },
  });
  const payload = res.data?.data ?? res.data;
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];
  const count = Number(payload?.count ?? payload?.total ?? rows.length);
  return { rows, count };
};

const updateGamicStatus = async (gamicId: string, status: GamicStatus) => {
  const api = apiService();
  return api.patch(`/gamic/status/${gamicId}`, { status });
};

const Reports: React.FC = () => {
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const { rows, count } = await fetchReports(page, search);
      setRows(rows);
      setCount(count);
    } catch (e: any) {
      setError(e?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    reload();
  };

  const onHide = async (gamicId: string) => {
    if (!window.confirm('Hide this gamic from the feed?')) return;
    setActionBusy(gamicId);
    try {
      await updateGamicStatus(gamicId, 'hold');
      await reload();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to hide');
    } finally {
      setActionBusy(null);
    }
  };

  const onRestore = async (gamicId: string) => {
    if (!window.confirm('Restore this gamic to the feed?')) return;
    setActionBusy(gamicId);
    try {
      await updateGamicStatus(gamicId, 'approved');
      await reload();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Failed to restore');
    } finally {
      setActionBusy(null);
    }
  };

  const grouped = useMemo(() => {
    // group rows by gamic_id so duplicate reports on the same gamic
    // show side-by-side with a single action row.
    const map: Record<string, ReportRow[]> = {};
    for (const r of rows) {
      (map[r.gamic_id] ||= []).push(r);
    }
    return Object.values(map);
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(count / 20));

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 4 }}>Reports</h2>
      <p style={{ color: '#888', marginTop: 0, marginBottom: 20 }}>
        Gamic reports submitted by users. Hidden gamics are automatically
        rolled to "hold" status when 3+ distinct users report them.
      </p>

      <form onSubmit={onSearchSubmit} style={{ marginBottom: 16 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by reporter name or gamic title…"
          style={{
            padding: '8px 12px',
            width: 360,
            border: '1px solid #ddd',
            borderRadius: 8,
          }}
        />
        <button
          type="submit"
          style={{
            marginLeft: 8,
            padding: '8px 16px',
            background: '#0066ff',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          Search
        </button>
      </form>

      {loading && <div>Loading…</div>}
      {error && <div style={{ color: 'crimson' }}>{error}</div>}

      {!loading && !error && grouped.length === 0 && (
        <div style={{ color: '#666', padding: 24 }}>No reports yet.</div>
      )}

      {grouped.map((group) => {
        const first = group[0];
        const isHidden = first.gamic_status === 'hold';
        const isRejected = first.gamic_status === 'rejected';
        return (
          <div
            key={first.gamic_id}
            style={{
              border: '1px solid #eee',
              borderRadius: 12,
              marginBottom: 16,
              padding: 16,
              background: isHidden
                ? '#fff7e6'
                : isRejected
                ? '#fff1f0'
                : '#fff',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
              }}
            >
              {first.gamic_image && (
                <img
                  src={first.gamic_image}
                  alt=""
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 8,
                    objectFit: 'cover',
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                  {first.gamic_title}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  by {first.gamic_owner_name} ({first.gamic_owner_email}) ·
                  status:{' '}
                  <strong
                    style={{
                      color: isHidden
                        ? '#d48806'
                        : isRejected
                        ? 'crimson'
                        : '#389e0d',
                    }}
                  >
                    {first.gamic_status}
                  </strong>{' '}
                  · {group.length} report{group.length === 1 ? '' : 's'}
                </div>
              </div>
              <a
                href={`/gamic/preview?id=${first.gamic_id}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  padding: '6px 12px',
                  border: '1px solid #0066ff',
                  borderRadius: 8,
                  color: '#0066ff',
                  textDecoration: 'none',
                  fontSize: 13,
                }}
              >
                View gamic
              </a>
              {isHidden ? (
                <button
                  onClick={() => onRestore(first.gamic_id)}
                  disabled={actionBusy === first.gamic_id}
                  style={{
                    padding: '6px 12px',
                    background: '#389e0d',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  Restore
                </button>
              ) : (
                <button
                  onClick={() => onHide(first.gamic_id)}
                  disabled={actionBusy === first.gamic_id}
                  style={{
                    padding: '6px 12px',
                    background: 'crimson',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  Hide
                </button>
              )}
            </div>

            <table style={{ width: '100%', fontSize: 13 }}>
              <thead style={{ textAlign: 'left', color: '#888' }}>
                <tr>
                  <th style={{ padding: '6px 0' }}>Reporter</th>
                  <th>Reason</th>
                  <th>Description</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {group.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '6px 0' }}>
                      {r.reporter_name}{' '}
                      <span style={{ color: '#999' }}>
                        ({r.reporter_email})
                      </span>
                    </td>
                    <td>{r.reason_category}</td>
                    <td style={{ color: '#555' }}>{r.description || '—'}</td>
                    <td style={{ color: '#999' }}>
                      {new Date(r.reported_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Prev
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default Reports;
